import React, { useState, useEffect } from 'react';
import { useSavedIdeasStore } from '../../../../stores/savedIdeasStore';
import { useContentPlanStore } from '../../../../stores/contentPlanStore';
import { Instagram, Youtube, Music } from 'lucide-react';
import type { Platform } from '../../../../types/calendar';

interface ContentSelectorProps {
  selectedContentId?: string;
  selectedContentType?: 'idea' | 'plan' | null;
  onSelect: (type: 'idea' | 'plan', id: string) => void;
}

export default function ContentSelector({ selectedContentId, selectedContentType, onSelect }: ContentSelectorProps) {
  const [activeTab, setActiveTab] = useState<'idea' | 'plan'>(selectedContentType || 'idea');
  const { savedIdeas } = useSavedIdeasStore();
  const { plans } = useContentPlanStore();

  // Set initial tab based on selected content
  useEffect(() => {
    if (selectedContentType) {
      setActiveTab(selectedContentType);
    }
  }, [selectedContentType]);

  const getPlatformIcon = (platform: Platform) => {
    switch (platform) {
      case 'instagram':
        return <Instagram className="h-5 w-5 text-pink-600" />;
      case 'youtube':
        return <Youtube className="h-5 w-5 text-red-600" />;
      case 'tiktok':
        return <Music className="h-5 w-5 text-black" />;
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Adicionar Conteúdo
        </label>
        <div className="flex space-x-4">
          <button
            type="button"
            onClick={() => setActiveTab('idea')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'idea'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Ideias Salvas
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('plan')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'plan'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Planos Salvos
          </button>
        </div>
      </div>

      <div className="max-h-60 overflow-y-auto">
        {activeTab === 'idea' ? (
          <div className="space-y-2">
            {savedIdeas.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhuma ideia salva encontrada
              </p>
            ) : (
              savedIdeas.map((idea) => (
                <div
                  key={idea.title}
                  onClick={() => onSelect('idea', idea.title)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedContentId === idea.title && selectedContentType === 'idea'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-2 mb-1">
                    {getPlatformIcon(idea.category as Platform)}
                    <h4 className="font-medium">{idea.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{idea.description}</p>
                  {idea.hashtags && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {idea.hashtags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {plans.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhum plano salvo encontrado
              </p>
            ) : (
              plans.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => onSelect('plan', plan.id)}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedContentId === plan.id && selectedContentType === 'plan'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <h4 className="font-medium mb-1">{plan.title}</h4>
                  <p className="text-sm text-gray-500">
                    Criado em {new Date(plan.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}